import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Product } from "@/data/products";

const colorHex: Record<string, string> = {
  nero: "#1c1c1c",
  bianco: "#f5f5f5",
  avorio: "#fffff0",
  grigio: "#9e9e9e",
  navy: "#001f5b",
  caramello: "#c68642",
  cipria: "#f2c8c4",
  cognac: "#8b4513",
  bordeaux: "#4a0018",
  beige: "#f5f0e8",
  verde: "#2d4a3e",
  oro: "#b8973a",
  nude: "#e8c9a0",
  blu: "#1e3a5f",
  azzurro: "#6fa8dc",
  cammello: "#c19a6b",
};

export interface FilterState {
  categoria: string;
  subCategoria: string;
  taglia: string;
  brand: string;
  colori: string[];
  prezzoMin: number;
  prezzoMax: number;
  scontoMin: number;
}

export function makeDefaultFilters(maxPrice: number): FilterState {
  return {
    categoria: "",
    subCategoria: "",
    taglia: "",
    brand: "",
    colori: [],
    prezzoMin: 0,
    prezzoMax: maxPrice,
    scontoMin: 0,
  };
}

function getTipo(category: string): string {
  if (category === "scarpe") return "scarpe";
  if (category === "accessori") return "accessori";
  return "abbigliamento";
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-stone-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3.5 text-[10px] tracking-widest uppercase font-semibold text-stone-900"
      >
        {title}
        {open ? (
          <ChevronUp className="size-3.5 text-stone-400" />
        ) : (
          <ChevronDown className="size-3.5 text-stone-400" />
        )}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

interface FilterPanelProps {
  baseProducts: Product[];
  filters: FilterState;
  onChange: (f: FilterState) => void;
}

export function FilterPanel({ baseProducts, filters, onChange }: FilterPanelProps) {
  const maxPrice = Math.max(...baseProducts.map((p) => p.price), 0);

  const categoriaProducts = filters.categoria
    ? baseProducts.filter((p) => getTipo(p.category) === filters.categoria)
    : baseProducts;

  const availableSubCategories = [
    ...new Set(categoriaProducts.map((p) => p.subCategory).filter(Boolean)),
  ].sort() as string[];

  const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL"];
  const availableSizes = [
    ...new Set(categoriaProducts.flatMap((p) => p.sizes ?? [])),
  ].sort((a, b) => {
    const ia = sizeOrder.indexOf(a);
    const ib = sizeOrder.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return parseFloat(a) - parseFloat(b);
  });

  const availableColors = [
    ...new Set(categoriaProducts.flatMap((p) => p.colors ?? [])),
  ];

  const availableBrands = [
    ...new Set(categoriaProducts.map((p) => p.brand).filter(Boolean)),
  ] as string[];

  const categoryOptions = [
    { value: "", label: "Tutti" },
    { value: "abbigliamento", label: "Abbigliamento" },
    { value: "scarpe", label: "Scarpe" },
    { value: "accessori", label: "Accessori" },
  ];

  const hasActiveFilters =
    filters.categoria ||
    filters.subCategoria ||
    filters.taglia ||
    filters.brand ||
    filters.colori.length > 0 ||
    filters.prezzoMin > 0 ||
    filters.prezzoMax < maxPrice ||
    filters.scontoMin > 0;

  function setCategoria(value: string) {
    onChange({ ...filters, categoria: value, subCategoria: "", taglia: "" });
  }

  function toggleColor(color: string) {
    const next = filters.colori.includes(color)
      ? filters.colori.filter((c) => c !== color)
      : [...filters.colori, color];
    onChange({ ...filters, colori: next });
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] tracking-[0.3em] uppercase font-semibold text-stone-900">
          Filtri
        </span>
        {hasActiveFilters && (
          <button
            onClick={() => onChange(makeDefaultFilters(maxPrice))}
            className="text-[9px] tracking-widest uppercase text-emerald-600 hover:text-emerald-700 transition-colors"
          >
            Azzera tutto
          </button>
        )}
      </div>

      <Section title="Categoria">
        <div className="flex flex-col gap-2.5">
          {categoryOptions.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategoria(cat.value)}
              className="flex items-center gap-2.5 group text-left"
            >
              <span
                className={cn(
                  "size-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                  filters.categoria === cat.value
                    ? "border-stone-900 bg-stone-900"
                    : "border-stone-300 group-hover:border-stone-600"
                )}
              >
                {filters.categoria === cat.value && (
                  <span className="size-1 rounded-full bg-white" />
                )}
              </span>
              <span
                className={cn(
                  "text-xs transition-colors",
                  filters.categoria === cat.value
                    ? "text-stone-900 font-medium"
                    : "text-stone-500 group-hover:text-stone-700"
                )}
              >
                {cat.label}
              </span>
            </button>
          ))}
        </div>
      </Section>

      {filters.categoria && availableSubCategories.length > 0 && (
        <Section title="Sottocategoria">
          <div className="flex flex-col gap-2.5">
            {availableSubCategories.map((sub) => (
              <button
                key={sub}
                onClick={() =>
                  onChange({
                    ...filters,
                    subCategoria: filters.subCategoria === sub ? "" : sub,
                  })
                }
                className="flex items-center gap-2.5 group text-left"
              >
                <span
                  className={cn(
                    "size-3.5 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                    filters.subCategoria === sub
                      ? "border-stone-900 bg-stone-900"
                      : "border-stone-300 group-hover:border-stone-600"
                  )}
                >
                  {filters.subCategoria === sub && (
                    <span className="size-1 bg-white" />
                  )}
                </span>
                <span
                  className={cn(
                    "text-xs capitalize transition-colors",
                    filters.subCategoria === sub
                      ? "text-stone-900 font-medium"
                      : "text-stone-500 group-hover:text-stone-700"
                  )}
                >
                  {sub}
                </span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {filters.categoria && availableSizes.length > 0 && (
        <Section title="Taglie">
          <div className="flex flex-wrap gap-1.5">
            {availableSizes.map((size) => (
              <button
                key={size}
                onClick={() =>
                  onChange({
                    ...filters,
                    taglia: filters.taglia === size ? "" : size,
                  })
                }
                className={cn(
                  "min-w-[2.25rem] h-9 px-2 text-xs border transition-colors",
                  filters.taglia === size
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-200 text-stone-500 hover:border-stone-500 hover:text-stone-800"
                )}
              >
                {size}
              </button>
            ))}
          </div>
        </Section>
      )}

      {availableBrands.length > 0 && (
        <Section title="Brand">
          <div className="flex flex-col gap-2.5">
            {availableBrands.map((b) => (
              <button
                key={b}
                onClick={() =>
                  onChange({ ...filters, brand: filters.brand === b ? "" : b })
                }
                className="flex items-center gap-2.5 group text-left"
              >
                <span
                  className={cn(
                    "size-3.5 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                    filters.brand === b
                      ? "border-stone-900 bg-stone-900"
                      : "border-stone-300 group-hover:border-stone-600"
                  )}
                >
                  {filters.brand === b && <span className="size-1 bg-white" />}
                </span>
                <span
                  className={cn(
                    "text-xs transition-colors",
                    filters.brand === b
                      ? "text-stone-900 font-medium"
                      : "text-stone-500 group-hover:text-stone-700"
                  )}
                >
                  {b}
                </span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {availableColors.length > 0 && (
        <Section title="Colori">
          <div className="flex flex-wrap gap-2">
            {availableColors.map((color) => {
              const hex = colorHex[color.toLowerCase()];
              const selected = filters.colori.includes(color);
              const isMulticolor = color === "multicolor";
              return (
                <button
                  key={color}
                  onClick={() => toggleColor(color)}
                  title={color.charAt(0).toUpperCase() + color.slice(1)}
                  aria-label={color}
                  className={cn(
                    "size-7 rounded-full transition-all flex-shrink-0",
                    selected
                      ? "ring-2 ring-offset-2 ring-stone-900"
                      : "ring-1 ring-stone-200 hover:ring-stone-400"
                  )}
                  style={
                    isMulticolor
                      ? {
                          background:
                            "conic-gradient(from 0deg, #e00, #e80, #ee0, #0c0, #00c, #c0c, #e00)",
                        }
                      : { background: hex || "#ccc" }
                  }
                />
              );
            })}
          </div>
          {filters.colori.length > 0 && (
            <p className="text-[9px] tracking-wide text-stone-400 mt-2 capitalize">
              {filters.colori.join(", ")}
            </p>
          )}
        </Section>
      )}

      <Section title="Sconto minimo">
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: 0, label: "Tutti" },
            { value: 20, label: "−20%" },
            { value: 30, label: "−30%" },
            { value: 50, label: "−50%" },
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => onChange({ ...filters, scontoMin: value })}
              className={cn(
                "px-3 py-1.5 text-xs border transition-colors",
                filters.scontoMin === value
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-stone-200 text-stone-500 hover:border-stone-500 hover:text-stone-800"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Prezzo">
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-[9px] tracking-widest uppercase text-stone-400 block mb-1.5">
                Min
              </label>
              <div className="flex items-center border border-stone-200 px-2.5 py-2 focus-within:border-stone-500 transition-colors">
                <span className="text-xs text-stone-400 mr-1">€</span>
                <input
                  type="number"
                  value={filters.prezzoMin}
                  min={0}
                  max={filters.prezzoMax - 1}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      prezzoMin: Math.max(0, parseInt(e.target.value) || 0),
                    })
                  }
                  className="w-full text-xs text-stone-900 bg-transparent focus:outline-none"
                />
              </div>
            </div>
            <span className="text-stone-300 text-xs pb-2.5">–</span>
            <div className="flex-1">
              <label className="text-[9px] tracking-widest uppercase text-stone-400 block mb-1.5">
                Max
              </label>
              <div className="flex items-center border border-stone-200 px-2.5 py-2 focus-within:border-stone-500 transition-colors">
                <span className="text-xs text-stone-400 mr-1">€</span>
                <input
                  type="number"
                  value={filters.prezzoMax}
                  min={filters.prezzoMin + 1}
                  max={maxPrice}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      prezzoMax: parseInt(e.target.value) || maxPrice,
                    })
                  }
                  className="w-full text-xs text-stone-900 bg-transparent focus:outline-none"
                />
              </div>
            </div>
          </div>
          <div className="h-px bg-stone-100 relative">
            <div
              className="absolute h-px bg-stone-900 top-0"
              style={{
                left: `${(filters.prezzoMin / maxPrice) * 100}%`,
                right: `${100 - (filters.prezzoMax / maxPrice) * 100}%`,
              }}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}
